import React, { useState } from "react";
import { Button, Card, Col, Form, ListGroup, Nav, Row, Tab } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { useAppApi } from "@/App";
import { DevForm } from "@components/Forms/Fields";
import { useSubmit } from "@components/Forms/Handlers";
import { Awesome, DevModal } from "@components/Helpers";
import { DefaultLayout } from "@components/Layouts";
import { url_reverse } from "@components/Utils";

export default function EventListScreen() {
    const { apiData, setApiData } = useAppApi(url_reverse("api:event:list"));
    const [showModal, setShowModal] = useState(false);
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState(localStorage.getItem("activeTab") || "settings");
    const [expandedYears, setExpandedYears] = useState({});

    const onFormSubmit = useSubmit();

    if (!apiData) return null;

    const handleTabChange = (tab) => {
        setActiveTab(tab);
        localStorage.setItem("activeTab", tab);
    };

    const handleModalOpen = (id) => {
        setShowModal(true);
        const newApiData = { ...apiData };
        newApiData.forms_data.main.data.event_group_id = id;
        setApiData(newApiData);
    };
    const handleModalClose = () => {
        const newApiData = { ...apiData };
        newApiData.forms_data.main.data = {};
        newApiData.forms_data.main.errors = {};
        setApiData(newApiData);
        setShowModal(false);
    };
    const fieldProps = { apiData, setApiData };

    const addEventModal = () => (
        <DevModal
            size="lg"
            show={showModal}
            onHide={handleModalClose}
            header={t("Создание Эвента")}
            body={
                <>
                    <DevForm.Field {...fieldProps} prefix="main" name="name" />
                    <DevForm.Field {...fieldProps} prefix="main" name="event_type" />
                    <DevForm.Field {...fieldProps} prefix="main" name="scheme" />
                </>
            }
            footer={
                <Button variant="primary" type="submit">
                    {t("Создать")}
                </Button>
            }
            renderWrapper={(children) => (
                <Form
                    onSubmit={(ev) =>
                        onFormSubmit(ev, {
                            submitPrefixes: ["main"],
                            onOK: (resp) => {
                                resp.forms_data.main.data = {};
                                resp.forms_data.main.errors = {};
                                setApiData(resp);
                                setShowModal(false);
                            },
                        })
                    }
                >
                    {children}
                </Form>
            )}
        />
    );

    const toggleYear = (groupId, year) => {
        const key = `${groupId}_${year}`;
        setExpandedYears((prev) => ({
            ...prev,
            [key]: !prev[key],
        }));
    };

    function renderYearBlock(groupKey, year, events) {
        const yearKey = `${groupKey}_${year}`;
        const isExpanded = expandedYears[yearKey];
        return (
            <React.Fragment key={yearKey}>
                <ListGroup.Item
                    action
                    onClick={() => toggleYear(groupKey, year)}
                    className="d-flex justify-content-between align-items-center"
                    style={{ cursor: "pointer", fontWeight: "bold", color: "#2d7dd0" }}
                >
                    <span>{year}</span>
                    <Awesome type={isExpanded ? "angle-double-up" : "angle-double-down"} className="ml-2" />
                </ListGroup.Item>
                {isExpanded &&
                    events.map((el) => (
                        <ListGroup.Item
                            key={el.id}
                            action
                            as={Link}
                            to={url_reverse("admin:event:items", { slug: el.slug })}
                            style={{ color: "#2d7dd0" }}
                        >
                            {el.name}
                        </ListGroup.Item>
                    ))}
            </React.Fragment>
        );
    }

    return (
        <DefaultLayout>
            <Tab.Container id="event-group-tab" activeKey={activeTab} onSelect={handleTabChange}>
                {addEventModal()}
                <Row>
                    <Col sm={3}>
                        <Card>
                            <Card.Body>
                                <Nav variant="pills" className="flex-column">
                                    {apiData?.event_group?.map((el) => (
                                        <Nav.Item key={el.id}>
                                            <Nav.Link eventKey={`event_group_${el.id}`}>{el.name}</Nav.Link>
                                        </Nav.Item>
                                    ))}
                                    <Nav.Item>
                                        <Nav.Link eventKey="settings">{t("Настройки")}</Nav.Link>
                                    </Nav.Item>
                                </Nav>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col sm={9}>
                        <Tab.Content>
                            {apiData?.group &&
                                Object.entries(apiData.group).map(([groupKey, value]) => (
                                    <Tab.Pane eventKey={`event_group_${groupKey}`} key={groupKey}>
                                        <Card>
                                            <Card.Header>
                                                <Row>
                                                    <Col>{t("Ивенты")}</Col>
                                                    <Col className="text-right">
                                                        <Button
                                                            variant="dark"
                                                            size="sm"
                                                            onClick={() => handleModalOpen(groupKey)}
                                                        >
                                                            {t("Добавить")}
                                                        </Button>
                                                    </Col>
                                                </Row>
                                            </Card.Header>
                                            <ListGroup>
                                                {value?.events?.map((el) => (
                                                    <ListGroup.Item
                                                        key={el.id}
                                                        action
                                                        as={Link}
                                                        to={url_reverse("admin:event:items", { slug: el.slug })}
                                                        style={{ color: "#2d7dd0" }}
                                                    >
                                                        {el.name}
                                                    </ListGroup.Item>
                                                ))}
                                                {value?.events_by_year &&
                                                    Object.entries(value.events_by_year)
                                                        .sort((a, b) => Number(b[0]) - Number(a[0])) // сортировка по убыванию годов
                                                        .map(([year, events]) =>
                                                            renderYearBlock(groupKey, year, events)
                                                        )}
                                            </ListGroup>{" "}
                                        </Card>
                                    </Tab.Pane>
                                ))}
                            {apiData?.unused_event_groups &&
                                apiData.unused_event_groups.map((el) => (
                                    <Tab.Pane eventKey={`event_group_${el}`} key={el}>
                                        <Card>
                                            <Card.Header>
                                                <Row>
                                                    <Col>{t("Ивенты")}</Col>
                                                    <Col className="text-right">
                                                        <Button
                                                            variant="dark"
                                                            size="sm"
                                                            onClick={() => handleModalOpen(el)}
                                                        >
                                                            {t("Добавить")}
                                                        </Button>
                                                    </Col>
                                                </Row>
                                            </Card.Header>
                                            <Card.Body />
                                        </Card>
                                    </Tab.Pane>
                                ))}
                            <Tab.Pane eventKey="settings">
                                <Card>
                                    <Card.Header>{t("Настройки")}</Card.Header>
                                    <Card.Body>
                                        <div>
                                            <Link to={url_reverse("admin:event:event_group:list")}>
                                                {t("Управление разделами")}
                                            </Link>
                                        </div>
                                        <div>
                                            <Link to={url_reverse("admin:event:schemes:list")}>
                                                {t("Управление шаблонами ивентов")}
                                            </Link>
                                        </div>
                                        <div>
                                            <Link to={url_reverse("admin:event:field_constructor:list")}>
                                                {t("Управление полями")}
                                            </Link>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Tab.Pane>
                        </Tab.Content>
                    </Col>
                </Row>
            </Tab.Container>
        </DefaultLayout>
    );
}
